import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import {
  useDashboardSummary,
  useDashboardByCounty,
  useDashboardByDistrict,
  useDashboardByOfficer,
  useDashboardTrends,
  useDashboardScopeSummary,
} from '@hooks/useDashboard';
import { useDashboardFilters } from '@hooks/useDashboardFilters';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { registeredApi } from '@services/api/analytics';
import { KenyaMap, CountyData } from '@components/KenyaMap';
import { DashboardFilterBar } from '@components/DashboardFilterBar';
import { DashboardKPIRow } from '@components/DashboardKPIRow';

const COLORS = {
  bg: '#0f1419',
  card: '#1a2332',
  cardBorder: '#2a3444',
  text: '#ffffff',
  muted: '#8b95a5',
  orange: '#FF6B4A',
  green: '#00D084',
  yellow: '#FAAD14',
};

const PERIOD_TO_DAYS: Record<string, number> = {
  today: 1, this_week: 7, last_7_days: 7, last_30_days: 30, drive_to_date: 90,
};

const TicksByHour: React.FC<{ hourlyData?: { hour: number; count: number; target?: number }[] }> = ({
  hourlyData,
}) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 7); // 07:00 to 18:00
  const targetPerHour = 50;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 110, gap: 0.5, px: 0.5, pb: 2, mb: 1 }}>
        {hours.map((h) => {
          const item = hourlyData?.find((d) => d.hour === h);
          const count = item?.count || 0;
          const pct = Math.min(100, (count / targetPerHour) * 100);
          const met = count >= targetPerHour;
          return (
            <Box key={h} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
              <Box
                sx={{
                  width: '75%',
                  minHeight: 3,
                  height: `${pct}%`,
                  backgroundColor: met ? COLORS.green : COLORS.orange,
                  borderRadius: 0.3,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -1,
                    left: -2,
                    right: -2,
                    height: 2,
                    backgroundColor: COLORS.yellow,
                    opacity: targetPerHour > 0 ? 1 : 0,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mb: 1.5 }}>
        {hours.map((h) => (
          <Typography key={h} sx={{ fontSize: '0.58rem', color: COLORS.muted, textAlign: 'center', flex: 1 }}>
            {String(h).padStart(2, '0')}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, backgroundColor: COLORS.green, borderRadius: 0.2 }} />
          <Typography sx={{ fontSize: '0.65rem', color: COLORS.muted }}>met target</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, backgroundColor: COLORS.orange, borderRadius: 0.2 }} />
          <Typography sx={{ fontSize: '0.65rem', color: COLORS.muted }}>below target</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 2, backgroundColor: COLORS.yellow }} />
          <Typography sx={{ fontSize: '0.65rem', color: COLORS.muted }}>target line</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const DashboardMainPage: React.FC = () => {
  const filters = useDashboardFilters();
  const { selectedCounty, selectedDistrict, selectedDivision, selectedLocation, period } = filters;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const summary = useDashboardSummary();
  const countyMetrics = useDashboardByCounty();
  const districtMetrics = useDashboardByDistrict(selectedCounty || null);
  const officerMetrics = useDashboardByOfficer();
  const trends = useDashboardTrends(PERIOD_TO_DAYS[period] || 1);
  // Reflects whatever county/district/division/location is currently selected —
  // this is what the KPI tiles use, NOT the always-national `summary` above.
  const scopeSummary = useDashboardScopeSummary({
    county: selectedCounty || undefined,
    district: selectedDistrict || undefined,
    division: selectedDivision || undefined,
    location: selectedLocation || undefined,
  });

  const counties = countyMetrics.data || [];
  const districts = districtMetrics.data || [];
  const officers = officerMetrics.data || [];
  const trendData = trends.data || [];

  // Every area with voter figures, registered or unregistered — including
  // DIASPORA and PRISONS, which only have registered voters (NRB has no
  // unregistered records for them).
  const registrationEnabledCounties = useMemo(
    () => new Set(counties.filter((c: any) => (c.unregistered_count || 0) > 0 || (c.registered_count || 0) > 0).map((c: any) => c.county)),
    [counties],
  );
  const dashboardFilters = useMemo(
    () => ({ ...filters, uniqueCounties: filters.uniqueCounties.filter((c) => registrationEnabledCounties.has(c)) }),
    [filters, registrationEnabledCounties],
  );

  // Build hourly data from trends if available, else empty
  const hourlyTicks = useMemo(() => {
    if (trendData && trendData.length) {
      // Map trend dates into hours
      return trendData.map((d: any, i: number) => ({
        hour: 7 + (i % 12),
        count: d.count || 0,
      }));
    }
    return [];
  }, [trendData]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'], exact: false });
  };

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Performance by district rows
  const districtRows = useMemo(() => {
    // Districts only for a selected county; otherwise one row per county.
    // (keepPreviousData would otherwise keep the last county's districts.)
    if (selectedCounty && districts.length > 0) return districts;
    if (counties.length > 0) {
      // Flatten counties into pseudo-districts if districts empty
      return counties.map((c: any) => ({
        district: c.county,
        county: c.county,
        registered_count: c.registered_count,
        unregistered_count: c.unregistered_count,
        total_count: c.total_count,
        registration_percentage: c.registration_percentage,
        pool_count: c.pool_count,
        converted_count: c.converted_count,
        conversion_percentage: c.conversion_percentage,
      }));
    }
    return [];
  }, [districts, counties, selectedCounty]);

  // Voter-roll figures: REGISTERED = on the latest IEBC registered-voters
  // list, UNREGISTERED = not on it (core_unregistered_voter).
  // All Kenya: the IEBC roll's own total, the figure analytics.sdics.tech
  // shows. The county rows also count people registered through the app
  // who aren't on the roll yet. Accounts without analytics access keep the sum.
  const roll = useQuery({
    queryKey: ['analytics', 'reg-rows', { county: '', constituency: '', ward: '', polling_centre: '', date_from: '', date_to: '' }],
    queryFn: () => registeredApi.rows({ county: '', constituency: '', ward: '', polling_centre: '', date_from: '', date_to: '' }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const rollTotal = roll.data ? (roll.data.rows ?? []).reduce((s, r) => s + (Number(r?.registered) || 0), 0) : null;
  const sumRegistered = districtRows.reduce((s, r: any) => s + (r.registered_count || 0), 0);
  const useRoll = !selectedCounty && !!rollTotal;
  const totalRegistered = useRoll ? rollTotal! : sumRegistered;
  // those registered via the app but not yet on the roll count as unregistered
  const totalUnregistered = districtRows.reduce((s, r: any) => s + (r.unregistered_count || 0), 0) + (useRoll ? sumRegistered - rollTotal! : 0);

  // Only block the whole page on summary/countyMetrics — these back the structural
  // pieces (KPI totals, table, map) and are proactively warmed every 300s by
  // core.tasks.warm_all_caches, so they're consistently fast. officerMetrics and
  // trends aren't warmed and feed isolated widgets (a KPI count, the hourly chart)
  // that already render sensible defaults while their own data streams in — gating
  // the entire page on them meant one N+1, occasionally-cold query blanked
  // everything else that was ready to show.
  const anyLoading = summary.isLoading || countyMetrics.isLoading;

  if (anyLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (summary.error) {
    return <Alert severity="error">Failed to load dashboard data</Alert>;
  }

  return (
    <Box sx={{ pb: 4, color: COLORS.text }}>
      <DashboardFilterBar filters={dashboardFilters} onRefresh={handleRefresh} />

      <DashboardKPIRow
        scopeData={scopeSummary.data}
        totalOfficers={summary.data?.total_officers}
        belowTargetCount={(officers as any[]).filter((o: any) => (o.registrations_today || 0) < 50).length || Math.max(0, (summary.data?.total_officers || 0))}
      />

      {/* ==================== MAIN TWO-COLUMN AREA ==================== */}
      <Grid container spacing={2}>
        {/* LEFT: Performance by district table */}
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 0.5,
            }}
          >
            {/* Title row */}
            <Box
              sx={{
                p: 1.25,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${COLORS.cardBorder}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Performance by subcounty
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: COLORS.muted, textDecoration: 'underline dotted' }}>
                  click a row to drill in
                </Typography>
              </Box>

              {/* Summary pills */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  { label: 'REGISTERED', value: totalRegistered.toLocaleString(), color: COLORS.green },
                  { label: 'UNREGISTERED', value: totalUnregistered.toLocaleString(), color: COLORS.orange },
                  { label: 'PERIOD', value: '+0', color: COLORS.green },
                  { label: 'DECLARED', value: '0', color: COLORS.muted },
                ].map((s) => (
                  <Box key={s.label} sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: COLORS.muted, letterSpacing: 0.3 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: s.color }}>
                      {s.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Table */}
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#251c0c' }}>
                    <TableCell
                      sx={{
                        color: COLORS.muted,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        borderBottom: `1px solid ${COLORS.cardBorder}`,
                        py: 0.75,
                        width: 200,
                      }}
                    >
                      UNIT
                    </TableCell>
                    {['REGISTERED', 'UNREGISTERED', 'PERIOD', 'DECLARED'].map((c) => (
                      <TableCell
                        key={c}
                        align="right"
                        sx={{
                          color: COLORS.muted,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          letterSpacing: 0.4,
                          borderBottom: `1px solid ${COLORS.cardBorder}`,
                          py: 0.75,
                        }}
                      >
                        {c}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        color: COLORS.muted,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        borderBottom: `1px solid ${COLORS.cardBorder}`,
                        py: 0.75,
                        width: 220,
                      }}
                    >
                      CONVERTED
                    </TableCell>
                    <TableCell
                      sx={{
                        color: COLORS.muted,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        borderBottom: `1px solid ${COLORS.cardBorder}`,
                        py: 0.75,
                        width: 140,
                      }}
                    >
                      CHIEFS ASSIGNED
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {districtRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: COLORS.muted, textAlign: 'center', py: 4, border: 'none' }}>
                        No data for this selection
                      </TableCell>
                    </TableRow>
                  )}
                  {districtRows.map((row: any, idx: number) => {
                    const unit = row.district || row.county || `Unit ${idx}`;
                    const reg = row.registered_count ?? 0;
                    const unreg = row.unregistered_count ?? 0;
                    const pct = row.conversion_percentage ?? 0;
                    const key = `${unit}-${idx}`;
                    const expanded = expandedRows[key];

                    return (
                      <React.Fragment key={key}>
                        <TableRow
                          onClick={() => toggleRow(key)}
                          sx={{
                            borderBottom: `1px solid ${COLORS.cardBorder}`,
                            cursor: 'pointer',
                            backgroundColor: idx % 2 === 1 ? 'rgba(26,35,50,0.4)' : COLORS.card,
                            '&:hover': { backgroundColor: '#252d3d' },
                          }}
                        >
                          <TableCell sx={{ color: COLORS.text, fontSize: '0.78rem', fontWeight: 600, py: 0.6, border: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {expanded ? (
                                <ExpandMore sx={{ fontSize: 14, color: COLORS.muted }} />
                              ) : (
                                <ChevronRight sx={{ fontSize: 14, color: COLORS.muted }} />
                              )}
                              {unit}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: COLORS.green, fontSize: '0.78rem', fontWeight: 700, py: 0.6, border: 'none' }}>
                            {reg.toLocaleString()}
                          </TableCell>
                          <TableCell align="right" sx={{ color: COLORS.orange, fontSize: '0.78rem', fontWeight: 600, py: 0.6, border: 'none' }}>
                            {unreg.toLocaleString()}
                          </TableCell>
                          <TableCell align="right" sx={{ color: COLORS.green, fontSize: '0.78rem', fontWeight: 700, py: 0.6, border: 'none' }}>
                            +0
                          </TableCell>
                          <TableCell align="right" sx={{ color: COLORS.muted, fontSize: '0.78rem', fontWeight: 600, py: 0.6, border: 'none' }}>
                            —
                          </TableCell>
                          <TableCell sx={{ py: 0.6, border: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, height: 4, backgroundColor: COLORS.bg, borderRadius: 0.5, position: 'relative' }}>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: `${Math.min(100, pct)}%`,
                                    backgroundColor: pct > 50 ? COLORS.green : COLORS.orange,
                                    borderRadius: 0.5,
                                    transition: 'width 0.3s ease',
                                  }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: pct > 0 ? COLORS.green : COLORS.muted,
                                  minWidth: 36,
                                  textAlign: 'right',
                                }}
                              >
                                {pct.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.72rem', py: 0.6, border: 'none' }}>
                            0 <Typography component="span" sx={{ color: COLORS.orange }}>unassigned</Typography>
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow sx={{ backgroundColor: COLORS.bg }}>
                            <TableCell colSpan={7} sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                              <Box
                                sx={{
                                  p: 1.5,
                                  backgroundColor: COLORS.card,
                                  border: `1px solid ${COLORS.cardBorder}`,
                                  borderRadius: 0.5,
                                }}
                              >
                                <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted }}>
                                  Detailed breakdown for <strong style={{ color: COLORS.text }}>{unit}</strong> — Sub-locations, officers on ground, and recent registrations.
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* RIGHT: Map + Ticks */}
        <Grid item xs={12} lg={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Conversion by county map */}
          <Card
            sx={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 0.5,
              flex: 1,
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: 280 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 1.25 }}>
                Conversion by county
              </Typography>
              <Box sx={{ height: 230 }}>
                <KenyaMap
                  countyData={(counties as any[]).map((c: any): CountyData => ({
                    name: c.county || '',
                    lat: 0,
                    lng: 0,
                    registered: c.converted_count || 0,
                    unregistered: c.unregistered_count || 0,
                    percentage: c.conversion_percentage || 0,
                  }))}
                  selectedCounty={selectedCounty}
                  onCountyClick={(county) => filters.selectCounty(county.name)}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Ticks by hour */}
          <Card
            sx={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 0.5,
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 1.5 }}>
                Ticks by hour - today
              </Typography>
              <TicksByHour hourlyData={hourlyTicks} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
